import { useCallback } from "react";
import { useParams } from "react-router";

import { Form, Formik } from "formik";

import { FormStatus } from "components/form";
import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import { Section } from "components/layout";
import {
  PlatformPublication,
  TiVoPublications,
  useUpdateVariantPublications,
} from "hooks/tivoApps";
import { TiVoDevicePlatform } from "hooks/tivoDevices";

import { VariantPublicationTable } from ".";
import {
  adaptPublicationsToRequest,
  getPublicationsInitialValues,
  getReleaseStagePublication,
  PublicationsFormButtons,
  PublicationSwitchCell,
} from "../../PublicationsList";
import { TiVoPublicationsForm } from "../../PublicationsList/types";
import { TEXTS } from "../constants";

interface VariantPublicationFormProps {
  platform: PlatformPublication;
  platforms: TiVoDevicePlatform[];
  publication: TiVoPublications;
}

export function VariantPublicationForm({
  platform,
  platforms,
  publication,
}: VariantPublicationFormProps) {
  const { id, variantId } = useParams();

  const updatePublications = useUpdateVariantPublications(id, variantId);
  const deviceData = useTiVoDeviceData();
  const handleSubmit = useCallback(
    async (values: TiVoPublicationsForm) => {
      const platforms = adaptPublicationsToRequest(values).find(
        ({ id }) => id === publication.id
      )?.platforms;
      if (!platforms) {
        console.error(
          "Failed to adapt the variant publication to the request."
        );
        return;
      }
      updatePublications.mutate({ platforms });
    },
    [publication, updatePublications]
  );

  return (
    <Section
      header={TEXTS.PUBLICATIONS.EDIT_TAB + " - " + platform.platformName}
    >
      <Formik
        enableReinitialize
        initialValues={getPublicationsInitialValues(
          platforms,
          [publication],
          platform.platformId
        )}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <VariantPublicationTable
              platformId={platform.platformId}
              publication={publication}
              withoutClientTypes={
                !platforms.find(({ id }) => id === platform.platformId)
                  ?.targetedByClientType
              }
            >
              {({
                variantReleaseStages,
                platformId,
                oemId,
                applicableReleaseStages,
                clientTypeId,
                releaseStage,
              }) => {
                const name =
                  `${variantId}.${platformId}.${oemId}` +
                  (clientTypeId ? `.${clientTypeId}` : "") +
                  `.${releaseStage}`;

                const targets = (publication.variant?.versions || []).find(
                  (version) => version.releaseStages?.includes(releaseStage)
                )?.targets;

                const releaseStagePublication = getReleaseStagePublication(
                  publication,
                  platformId,
                  oemId,
                  clientTypeId
                );

                const isTargetValid = clientTypeId
                  ? deviceData
                      .getClientTypeIds([platformId], [oemId], targets)
                      .includes(clientTypeId)
                  : deviceData.getOemIds([platformId], targets).includes(oemId);

                return (
                  <PublicationSwitchCell
                    key={releaseStage}
                    name={name}
                    releaseStage={releaseStage}
                    variantReleaseStages={variantReleaseStages}
                    applicableReleaseStages={applicableReleaseStages}
                    publication={releaseStagePublication}
                    isTargetValid={isTargetValid}
                  />
                );
              }}
            </VariantPublicationTable>
            <PublicationsFormButtons {...formikProps} />
            <FormStatus
              submitMutations={[updatePublications]}
              withErrorDetails
            />
          </Form>
        )}
      </Formik>
    </Section>
  );
}
